import { ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import {
  hasTextSelection,
  workflowCanvasHasNodeSelection,
  workflowCanvasShortcutAvailable,
  workflowModelFromContext,
  type WorkflowClipboardOps,
} from './workflow-toolbar-action.helpers';
import {
  reportWorkflowCopy,
  setWorkflowClipboard,
  workflowSelectionPayload,
} from './workflow-node-clipboard';

/**
 * Copies the selected nodes onto the workflow clipboard.
 *
 * No toolbar button: copying has no visible result to justify one, and the
 * paste it sets up is offered next to it in the command palette.
 */
export class WorkflowCopySelectionAction extends WorkflowEditorToolbarActionBase {
  title = 'Copy nodes';
  override description = 'Copy the selected nodes';
  group = 'Workflow';
  icon = 'copy';
  shortcut = ['ctrl', 'c'];

  constructor(private readonly ops: WorkflowClipboardOps) {
    super();
  }

  /** Text highlighted anywhere on the page is a copy the browser should do. */
  shortcutAvailable(): boolean {
    return (
      workflowCanvasShortcutAvailable(this.ops) && hasTextSelection() === false
    );
  }

  /**
   * Nodes only — a trigger binds a row that lives outside the workflow, so
   * there is nothing here to copy for one.
   */
  override applicable(context: ApplicableContext): boolean {
    return workflowCanvasHasNodeSelection(context);
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    const model = workflowModelFromContext(context);
    const payload = model === null ? null : workflowSelectionPayload(model);
    if (payload === null) {
      return ok({});
    }
    setWorkflowClipboard(payload);
    reportWorkflowCopy(payload);
    return ok({});
  }
}
